export const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  payload: any;
  constructor(
    public status: number,
    public code: string,
    payload?: any
  ) {
    super(
      typeof payload === "object" && payload && typeof payload.message === "string"
        ? payload.message
        : code
    );
    this.payload = payload;
  }
}

const DEFAULT_TIMEOUT_MS = 60_000;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<T> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") {
      throw new ApiError(0, "TIMEOUT", { message: "Request timed out." });
    }
    throw new ApiError(0, "NETWORK_ERROR", {
      message:
        navigator.onLine === false
          ? "You appear to be offline."
          : "Couldn't reach the server. Check your connection.",
    });
  } finally {
    window.clearTimeout(timer);
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? "REQUEST_FAILED", data ?? text);
  }
  return data as T;
}

function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(p: string) => request<T>("GET", p),
  post: <T>(p: string, body?: unknown) => request<T>("POST", p, body),
  patch: <T>(p: string, body?: unknown) => request<T>("PATCH", p, body),
  del: <T>(p: string, body?: unknown) => request<T>("DELETE", p, body),
};
